import React from 'react';
import { css } from '@linaria/core';
import { brand, theme, findColorFromCSSVariable } from '@terveystalo/design-system-theme';
import forwardRefAs from 'forward-ref-as';
import { Icon } from '../../atoms/Icon';
import { classnames } from '../../utils';
import { calcPadding3_5 } from '../../css-utils/calculation';
/**
 * Component for displaying an input field with label and validation messages. The input field doesn't perform any validations, but it forwards
 * a ref so e.g. focus event can be triggered.
 */
export const TextFieldInput = forwardRefAs((props, ref) => {
    const { as: Component = 'input', id, icon, invalid, valid, grayBackground, className, disabled, describedByIds, ...rest } = props;
    const isTextArea = Component === 'textarea';
    const additionalProps = isTextArea ? { rows: 3 } : {};
    return (React.createElement("div", { className: css `
        /* Allow the width to scale based on the container */
        width: 100%;
        position: relative;
        display: flex;
        align-items: flex-start;
      ` },
        React.createElement(Component, { id: id, ref: ref, disabled: disabled, "aria-describedby": describedByIds, ...additionalProps, className: classnames(css `
            width: 100%;
            background-color: ${brand.textField.color.default.background.onLight};
            border: ${theme.border.width.xxs} solid ${brand.textField.color.default.border};
            border-radius: ${brand.textField.border.radius};
            padding: ${calcPadding3_5(theme.typography.lineHeight.body.m, '1', 'xxs')};

            font-family: ${brand.textField.font.family};
            font-size: ${brand.textField.font.size};
            line-height: ${brand.textField.font.lineHeight};
            letter-spacing: ${brand.textField.font.letterSpacing};
            font-weight: ${brand.textField.font.weight};

            /* iOS Safari does weird things to the input field, skip the default styling */
            -webkit-appearance: none;

            &::-ms-clear {
              display: none;
            }

            &:disabled {
              color: ${brand.textField.color.disabled.content};
              border-color: ${brand.textField.color.disabled.border};
              /* Fix colors with iOS/Safari */
              -webkit-text-fill-color: ${brand.textField.color.disabled.content};
              opacity: 1;
            }

            &:not(&:disabled) {
              &:hover {
                border: ${theme.border.width.xxs} solid ${brand.textField.color.active.border};
              }
              &:focus,
              &:active {
                outline: 0;
                border: ${theme.border.width.xxs} solid transparent;
                /* Use box-shadow to prevent the height from jumping due to the wider border in focus styles   */
                box-shadow: 0 0 0 ${theme.border.width.xs} ${brand.textField.color.active.border};
              }
            }
          `, grayBackground &&
                css `
              background-color: ${brand.textField.color.default.background.onDark};
            `, icon &&
                css `
              padding-left: ${theme.spacing['3']};
            `, invalid &&
                css `
              padding-right: ${theme.spacing['3']};
              border: ${theme.border.width.xxs} solid ${brand.textField.color.invalid.border};
              &:not(&:disabled) {
                &:hover {
                  border: ${theme.border.width.xxs} solid ${brand.textField.color.invalid.border};
                }
                &:focus {
                  outline: 0;
                  border: ${theme.border.width.xxs} solid transparent;
                  /* Use box-shadow to prevent the height from jumping due to the wider border in focus styles   */
                  box-shadow: 0 0 0 ${theme.border.width.xs} ${brand.textField.color.invalid.border};
                }
              }
            `, className), ...rest }),
        icon && (React.createElement(Icon, { name: icon, color: findColorFromCSSVariable(disabled ? brand.textField.color.disabled.icon : brand.textField.color.disabled.content), className: css `
            position: absolute;
            left: 0;
            top: ${theme.spacing['1']};
            margin-left: ${theme.spacing['1']};
          ` })),
        (invalid || valid) && (React.createElement(Icon, { name: invalid ? 'Info' : 'Check', color: findColorFromCSSVariable(invalid ? brand.textField.color.invalid.content : brand.textField.color.success.content), className: css `
            position: absolute;
            right: 0;
            top: ${theme.spacing['1']};
            margin-right: ${theme.spacing['1']};
          ` }))));
});


import "../../../css/molecules/TextField/Input.css";
